import React, { useContext, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { net } from "../../../../database";
import "./profile.css";
import { LanguageContext, UserContext } from "../../../../Context";
import { User, Calendar, Phone, MapPin, Shield, Building, Lock, Camera, Mail, Briefcase, Clock, Award, XCircle, Loader2 } from "lucide-react";
import Avatar from "../../../topbar/components/Avatar";
import HtTabs from "../../components/HtTabs";
import Lang from "../../components/Lang";
import DateChoice from "../../components/DateChoice";
import LeaveRequestDialog from "./LeaveRequestDialog";
import useDBO from "../../hooks/useDBO";
import mut from "../../hooks/mut";
import { usePrimaryColor } from "../../components/PrimaryColorContext";


/**
 * Profile page component - displays user profile with tabs for general info,
 * employment history, and leave requests. Includes a circular progress indicator
 * showing profile completeness.
 * @author Forge
 * @date 2026-04-07
 * @version 2.0
 */
export default function Profile(props) {
  let { user, setUser } = useContext(UserContext)
  const { l, language } = useContext(LanguageContext);
  const { primaryColor } = usePrimaryColor();
  const { tab } = useParams();
  const navigate = useNavigate();
  const [passwordExpanded, setPasswordExpanded] = useState(false);
  const [bankExpanded, setBankExpanded] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [managerName, setManagerName] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Tab configuration: map URL param to tab index
  const tabConfig = {
    "general": 0,
    "history": 1,
    "leave": 2
  };

  const tabLabels = ["General Info", "Employment History", "Leave Requests"];
  const tabPaths = ["general", "history", "leave"];

  // Get current tab index from URL or default to 0
  const getTabIndexFromUrl = () => {
    if (tab && tabConfig[tab] !== undefined) {
      return tabConfig[tab];
    }
    return 0;
  };

  // Handle tab change - update URL
  const handleTabChange = (index) => {
    const path = tabPaths[index];
    if (path) {
      navigate(`/profile/${path}`);
    }
  };

  /**
   * Fetches leave requests from the API with 30s polling.
   * Returns { get, add, update, remove, loading, refresh, ... } from useDBO.
   * @author Forge
   * @date 2026-04-08
   * @version 2.0
   */
  const leaveRequests = useDBO("/leave-requests", 30);

  React.useEffect(() => {
    net
      .get("/user/" + user._id)
      .then((r) => {
        setUser(r);
        if (r.reportsTo) {
          net.get("/user/" + r.reportsTo).then((mgr) => {
            if (mgr) setManagerName(mgr.fullName || mgr.name);
          });
        }
      });
  }, [user._id, setUser]);

  /**
   * Moves the HtTabs header (nav buttons) into the profile header bar
   * so the tab labels appear next to the "Profile" title
   * @author Forge
   * @date 2026-04-07
   * @version 1.0
   */
  React.useEffect(() => {
    const timer = setTimeout(() => {
      const slot = document.getElementById("profile-header-tabs-slot");
      const bodyTabs = document.querySelector(".profile-body .ht-tabs-header-wrapper");
      if (slot && bodyTabs) {
        slot.innerHTML = "";
        slot.appendChild(bodyTabs);
      }
    }, 50);
    return () => clearTimeout(timer);
  });

  const update = (field, data) => {
    net
      .post("/user/" + props.user._id, { [field]: data })
      .then((r) => setUser({ ...user, [field]: data }));
  };

  /**
   * Handles avatar file upload using native FormData and fetch.
   * Updates user context with new image on success.
   * @param {File} file - The selected image file
   * @author Forge
   * @date 2026-04-10
   * @version 1.0
   */
  const handleAvatarUpload = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch('/fileupload/' + user._id, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('apiKey')}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success && data.image) {
          setUser({ ...user, image: data.image });
        }
      } else {
        alert(l["uploadFailed"]?.[language] || "Upload failed");
      }
    } catch (err) {
      console.error(err);
      alert(l["uploadFailed"]?.[language] || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  /**
   * Calculates profile completeness as a percentage based on filled fields
   * @returns {number} Percentage complete (0-100)
   * @author Forge
   * @date 2026-04-07
   * @version 1.0
   */
  const getProfileCompleteness = () => {
    const fields = [
      user.fullName || user.name,
      user.phone,
      user.address,
      user.dateOfBirth,
      user.emergency,
      user.email,
      user.bankAccountBank,
      user.bankAccountNumber,
      user.bankAccountName
    ];
    const filled = fields.filter(f => f && f.toString().trim() !== "").length;
    return Math.round((filled / fields.length) * 100);
  };

  /**
   * Returns the list of incomplete profile fields
   * @returns {Array} Array of objects with field name and label
   * @author Forge
   * @date 2026-04-07
   * @version 1.0
   */
  const getIncompleteFields = () => {
    const checks = [
      { key: "fullName", label: "Full Name", value: user.fullName || user.name },
      { key: "phone", label: "Phone", value: user.phone },
      { key: "address", label: "Address", value: user.address },
      { key: "dateOfBirth", label: "Date of Birth", value: user.dateOfBirth },
      { key: "emergency", label: "Emergency Contact", value: user.emergency },
      { key: "email", label: "Email", value: user.email },
      { key: "bankAccountBank", label: "Bank Name", value: user.bankAccountBank },
      { key: "bankAccountNumber", label: "Bank Account", value: user.bankAccountNumber },
      { key: "bankAccountName", label: "Bank Account Name", value: user.bankAccountName },
    ];
    return checks.filter(f => !f.value || f.value.toString().trim() === "");
  };

  if (!user) return <div className="loading-state">Loading...</div>;

const completeness = getProfileCompleteness();
const incompleteFields = getIncompleteFields();

const profileLayout = [
    { name: "fullName", type: "text", label: "Full name", icon: <User size={16} /> },
    { name: "email", type: "text", label: "Email", icon: <Mail size={16} /> },
    { name: "phone", type: "text", label: "Phone", icon: <Phone size={16} /> },
    { name: "address", type: "text", label: "Street Address", icon: <MapPin size={16} /> },
    { name: "dateOfBirth", type: "date", label: "Date of birth", icon: <Calendar size={16} /> },
    { name: "emergency", type: "text", label: "Emergency contact", icon: <Shield size={16} /> }
  ];

  const bankLayout = [
    { name: "bankAccountBank", type: "text", label: "Bank Name", icon: <Building size={16} /> },
    { name: "bankAccountNumber", type: "text", label: "Bank account number", icon: <Lock size={16} /> },
    { name: "bankAccountName", type: "text", label: "Name on bank account", icon: <User size={16} /> }
  ];

  /**
   * Circular progress indicator SVG component
   * @param {Object} props - Component props
   * @param {number} props.percentage - The percentage to display (0-100)
   * @param {number} props.size - SVG size in pixels
   * @param {number} props.strokeWidth - Stroke width in pixels
   * @author Forge
   * @date 2026-04-07
   * @version 1.0
   */
  const CircularProgress = ({ percentage, size = 140, strokeWidth = 10 }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (percentage / 100) * circumference;
    const center = size / 2;

    const getColor = (pct) => {
      if (pct >= 80) return { stroke: "#10b981", bg: "#ecfdf5", text: "#059669" };
      if (pct >= 50) return { stroke: "#0284c7", bg: "#f0f9ff", text: "#0369a1" };
      if (pct >= 25) return { stroke: "#f59e0b", bg: "#fffbeb", text: "#d97706" };
      return { stroke: "#ef4444", bg: "#fef2f2", text: "#dc2626" };
    };

    const colors = getColor(percentage);

    return (
      <div className="circular-progress-wrapper" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="circular-progress-svg">
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#e2e8f0"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={colors.stroke}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
            className="circular-progress-ring"
          />
        </svg>
        <div className="circular-progress-text">
          <span className="circular-progress-value" style={{ color: colors.text }}>{percentage}%</span>
          <span className="circular-progress-label">Complete</span>
        </div>
      </div>
    );
  };

  /**
   * General Info tab - premium layout with avatar, stats, personal info and bank details
   * @author Forge
   * @date 2026-04-07
   * @version 2.0
   */
  const generalInfoContent = (
    <div className="profile-main-grid p-0 gap-6">
      {/* Left Sidebar - Avatar & Quick Stats */}
      <div className="profile-sidebar home-widget p-6">
          <div className="avatar-card mb-6">
          <div
            className={`avatar-upload-overlay ${user.image ? 'has-image' : ''}`}
            onClick={() => !uploading && document.getElementById('avatar-upload')?.click()}
          >
            <Avatar
              alt={user.name}
              src={`/images/${user.image}`}
              className="avatar-image"
            />
            <div className="avatar-upload-icon">
              {uploading ? <Loader2 size={24} className="animate-spin" /> : <Camera size={24} />}
            </div>
            <input
              id="avatar-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleAvatarUpload(file);
                e.target.value = '';
              }}
            />
          </div>
          <div className="avatar-info mt-4 text-center">
            <h3 className="text-lg font-bold text-slate-900">{user.fullName || user.name}</h3>
            <p className="text-slate-500 text-sm mt-0.5">{user.position || "No position set"}</p>
            {user.email && <p className="text-slate-400 text-xs mt-1">{user.email}</p>}
          </div>
        </div>

        <div className="sidebar-divider" />

        <div className="sidebar-details-list">
          <div className="sidebar-detail-item">
            <div className="sidebar-detail-icon"><Award size={15} /></div>
            <div className="sidebar-detail-content">
              <span className="sidebar-detail-label">Role</span>
              <span className="sidebar-detail-value">{user.roles?.[0] || "N/A"}</span>
            </div>
          </div>
          {user.reportsTo && (
            <div className="sidebar-detail-item">
              <div className="sidebar-detail-icon"><User size={15} /></div>
              <div className="sidebar-detail-content">
                <span className="sidebar-detail-label">Manager</span>
                <span className="sidebar-detail-value">{managerName || "Loading..."}</span>
              </div>
            </div>
          )}
          <div className="sidebar-detail-item">
            <div className="sidebar-detail-icon"><Clock size={15} /></div>
            <div className="sidebar-detail-content">
              <span className="sidebar-detail-label">Member Since</span>
              <span className="sidebar-detail-value">{new Date(user.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
          <div className="sidebar-detail-item">
            <div className="sidebar-detail-icon"><Briefcase size={15} /></div>
            <div className="sidebar-detail-content">
              <span className="sidebar-detail-label">Last Login</span>
              <span className="sidebar-detail-value">{user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : "N/A"}</span>
            </div>
          </div>
        </div>

        <div className="sidebar-divider" />

        <div className="password-section">
          <button className="expand-button w-full flex justify-between items-center" onClick={() => setPasswordExpanded(!passwordExpanded)}>
            <span className="font-semibold text-slate-700 text-sm">Change Password</span>
            <Lock size={14} className="text-slate-400" />
          </button>
          {passwordExpanded && (
            <div className="password-form mt-4 gap-2 flex flex-col">
              <input type="password" className="profile-input" placeholder="Current password" />
              <input type="password" className="profile-input" placeholder="New password" />
              <input type="password" className="profile-input" placeholder="Confirm new password" />
              <button className="profile-save-btn mt-2">Save Changes</button>
            </div>
          )}
        </div>
      </div>

      {/* Right Content - Personal Info & Bank */}
      <div className="profile-content flex flex-col gap-6">
        <div className="home-widget p-6">
          <div className="section-header mb-5">
            <div className="section-header-icon"><User size={18} /></div>
            <h2 className="section-title">Personal Information</h2>
          </div>
          <div className="form-fields-grid">
            {profileLayout.map((p) => (
              <div className="profile-field" key={p.name}>
                <div className="field-label flex items-center gap-2">
                  <span className="field-label-icon">{p.icon}</span>
                  <span className="field-label-text">{p.label}</span>
                </div>
                {p.name === "dateOfBirth" ? (
                  <DateChoice
                    value={user[p.name] || ""}
                    setValue={(val) => {
                      setUser({ ...user, [p.name]: val });
                      update(p.name, val);
                    }}
                    fullWidth={true}
                    className="profile-date-choice mt-1.5"
                    noFuture={true}
                  />
                ) : (
                  <input
                    type={p.type}
                    value={user[p.name] || ""}
                    onChange={(e) => setUser({ ...user, [p.name]: e.target.value })}
                    onBlur={(e) => update(p.name, e.target.value)}
                    className="profile-input mt-1.5"
                    {...(p.type === "date" && { InputLabelProps: { shrink: true } })}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="home-widget p-6">
          <button className="expand-button w-full flex justify-between items-center" onClick={() => setBankExpanded(!bankExpanded)}>
            <div className="section-header mb-0">
              <div className="section-header-icon"><Building size={18} /></div>
              <h2 className="section-title">Bank Details</h2>
            </div>
            <span className={`expand-chevron ${bankExpanded ? 'expanded' : ''}`}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </span>
          </button>
          {bankExpanded && (
            <div className="form-fields-grid mt-5">
              {bankLayout.map((p) => (
                <div className="profile-field" key={p.name}>
                  <div className="field-label flex items-center gap-2">
                    <span className="field-label-icon">{p.icon}</span>
                    <span className="field-label-text">{p.label}</span>
                  </div>
                  <input
                    type={p.type}
                    value={user[p.name] || ""}
                    onChange={(e) => setUser({ ...user, [p.name]: e.target.value })}
                    onBlur={(e) => update(p.name, e.target.value)}
                    className="profile-input mt-1.5"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  /** @todo Employment History tab - to be implemented */
  const employmentHistoryTab = (
    <div className="home-widget p-6">
      <h2 className="text-xl font-bold text-slate-900 mb-4">Employment History</h2>
      <p className="text-slate-500">Coming soon...</p>
    </div>
  );

  /**
   * Handles successful leave request creation.
   * Adds the server-returned request to the local useDBO collection.
   * @param {Object} data - Server response containing the new leave request with _id
   * @author Forge
   * @date 2026-04-08
   * @version 2.0
   */
  const handleLeaveRequest = (data) => {
    if (data) leaveRequests.add(data);
  };

  /**
   * Cancels a pending leave request by calling the cancel API endpoint.
   * Updates the local useDBO collection with the server response.
   * @param {string} id - The _id of the leave request to cancel
   * @author Forge
   * @date 2026-04-08
   * @version 1.0
   */
  const handleCancelRequest = async (id) => {
    if (!window.confirm(l["cancelConfirm"][language])) return;
    setCancellingId(id);
    try {
      const res = await mut("put", `/leave-requests/${id}/cancel`);
      if (res) leaveRequests.update(res);
    } finally {
      setCancellingId(null);
    }
  };

  /**
   * Renders a single leave request card with status-based styling.
   * Shows a Cancel button for pending requests owned by the current user.
   * @param {Object} request - Leave request data from the API
   * @author Forge
   * @date 2026-04-08
   * @version 2.0
   */
  const renderLeaveCard = (request) => {
    const statusConfig = {
      pending: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-400" },
      approved: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-400" },
      declined: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", dot: "bg-red-400" },
      cancelled: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-500", dot: "bg-slate-400" }
    };
    const cfg = statusConfig[request.status] || statusConfig.pending;
    const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString(language === 0 ? "en-US" : "vi-VN", {
      year: "numeric", month: "short", day: "numeric"
    });

    return (
      <div key={request._id} className={`leave-card ${cfg.bg} border ${cfg.border} rounded-2xl p-5 transition-all duration-200 hover:shadow-md`}>
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2">
            <span className={`${cfg.dot} w-2.5 h-2.5 rounded-full inline-block`} />
            <span className={`${cfg.text} text-xs font-bold uppercase tracking-wider`}><Lang w={request.status} /></span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-slate-400 text-xs"><Lang w="applied" />: {formatDate(request.createdAt)}</span>
            {request.status === "pending" && (
              <button
                onClick={() => handleCancelRequest(request._id)}
                disabled={cancellingId === request._id}
                className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold text-red-600 bg-red-100 hover:bg-red-200 rounded-lg transition-colors duration-150 disabled:opacity-50"
              >
                {cancellingId === request._id ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <XCircle size={12} />
                )}
                <Lang w="cancelRequest" />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-slate-400" />
            <span className="text-sm font-semibold text-slate-700">{formatDate(request.startDate)}</span>
          </div>
          <span className="text-slate-300">→</span>
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-slate-400" />
            <span className="text-sm font-semibold text-slate-700">{formatDate(request.endDate)}</span>
          </div>
        </div>
        <p className="text-slate-600 text-sm">{request.reason}</p>
        {request.status === "declined" && request.declinedReason && (
          <div className="mt-3 pt-3 border-t border-red-200">
            <p className="text-red-600 text-xs font-semibold uppercase tracking-wider mb-1"><Lang w="declineReason" /></p>
            <p className="text-red-500 text-sm">{request.declinedReason}</p>
          </div>
        )}
      </div>
    );
  };

  /**
   * Leave requests tab content - displays grouped leave requests with create dialog.
   * Fetches data via useDBO and persists via mut.
   * @author Forge
   * @date 2026-04-08
   * @version 2.0
   */
  const leaveRequestsTab = (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-slate-900"><Lang w="leaveRequests" /></h2>
        <button
          className="flex items-center gap-2 px-5 py-2.5 text-white rounded-xl font-bold text-sm transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
          
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = primaryColor}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = primaryColor}
          onClick={() => {
            if (!user.reportsTo) {
              alert(l["managerRequired"][language]);
              return;
            }
            setLeaveDialogOpen(true);
          }}
        >
          + <Lang w="requestLeave" />
        </button>
      </div>

      {leaveRequests.loading && !leaveRequests.get && (
        <div className="home-widget p-12 text-center flex flex-col items-center gap-3">
          <Loader2 size={32} className="animate-spin text-slate-300" />
          <p className="text-slate-400"><Lang w="loadingLeave" /></p>
        </div>
      )}

      {leaveRequests.get && <>
        {leaveRequests.get.filter(r => r.status === "pending").length > 0 && (
          <div>
            <h3 className="text-sm font-bold text-amber-700 uppercase tracking-wider mb-3"><Lang w="pending" /></h3>
            <div className="flex flex-col gap-3">
              {leaveRequests.get.filter(r => r.status === "pending").map(renderLeaveCard)}
            </div>
          </div>
        )}

        {leaveRequests.get.filter(r => r.status === "approved").length > 0 && (
          <div>
            <h3 className="text-sm font-bold text-emerald-700 uppercase tracking-wider mb-3"><Lang w="approved" /></h3>
            <div className="flex flex-col gap-3">
              {leaveRequests.get.filter(r => r.status === "approved").map(renderLeaveCard)}
            </div>
          </div>
        )}

        {leaveRequests.get.filter(r => r.status === "declined").length > 0 && (
          <div>
            <h3 className="text-sm font-bold text-red-700 uppercase tracking-wider mb-3"><Lang w="declined" /></h3>
            <div className="flex flex-col gap-3">
              {leaveRequests.get.filter(r => r.status === "declined").map(renderLeaveCard)}
            </div>
          </div>
        )}

        {leaveRequests.get.filter(r => r.status === "cancelled").length > 0 && (
          <div>
            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3"><Lang w="cancelled" /></h3>
            <div className="flex flex-col gap-3">
              {leaveRequests.get.filter(r => r.status === "cancelled").map(renderLeaveCard)}
            </div>
          </div>
        )}

        {leaveRequests.get.length === 0 && (
          <div className="home-widget p-12 text-center">
            <p className="text-slate-400 text-lg"><Lang w="noLeaveRequests" /></p>
          </div>
        )}
      </>}

      <LeaveRequestDialog
        open={leaveDialogOpen}
        onClose={() => setLeaveDialogOpen(false)}
        manager={user.reportsTo}
        onSuccess={handleLeaveRequest}
      />
    </div>
  );

  const tabComponents = [generalInfoContent, employmentHistoryTab, leaveRequestsTab];

  return (
    <div className="profile-dashboard">
      {/* Header: Title left, tab nav pulled here via CSS */}
      <div className="profile-header">
        <h1 className="profile-page-title">Profile</h1>
        <div className="profile-header-tabs-nav" id="profile-header-tabs-slot" />
      </div>

      {/* Profile Completeness Section */}
      <div className="profile-completeness-section">
        <div className="completeness-card">
          <div className="completeness-left">
            <CircularProgress percentage={completeness} size={68} strokeWidth={6} />
          </div>
          <div className="completeness-right">
            <h3 className="completeness-title">
              {completeness === 100
                ? "Profile Complete"
                : "Complete Your Profile"
              }
            </h3>
            <p className="completeness-description">
              {completeness === 100
                ? "All profile fields are filled in. Great job!"
                : `You have ${incompleteFields.length} field${incompleteFields.length !== 1 ? "s" : ""} remaining to complete your profile.`
              }
            </p>
            {incompleteFields.length > 0 && incompleteFields.length <= 5 && (
              <div className="completeness-missing-tags">
                {incompleteFields.map((f) => (
                  <span key={f.key} className="completeness-missing-tag">{f.label}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs: nav is visually moved to header via CSS, panels render here */}
      <div className="profile-body">
        <HtTabs 
          labels={tabLabels} 
          components={tabComponents} 
          defaultValue={getTabIndexFromUrl()}
          updateValue={handleTabChange}
        />
      </div>
    </div>
  );
}
